/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILinkProps, ILinkStyles, Link } from '@fluentui/react/lib/Link';
import { ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { findFirstNodes } from 'src/core/utils/tree';
import { EntityTree } from 'src/luis/modules/entities/components/EntityTree';
import { useEntityTreeActions } from 'src/luis/modules/entities/hooks/useEntityTreeActions';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { HierarchicalEntityStore } from 'src/luis/types/stores/entities/HierarchicalEntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';
import { DeleteEntityModal } from 'src/luis/verticals/deleteEntityModal/DeleteEntityModal';
import { EntityTreeTopBar } from 'src/luis/verticals/labelingSidePane/components/EntityTreeTopBar';
import { getMatchedSpans } from 'src/luis/verticals/labelingSidePane/utils';

const messages = defineMessages({
    emptyText: {
        id: 'luis.intentLabelingSidePane.emptyText',
        defaultMessage: 'You have no labeled entities.'
    },
    noMatchText: {
        id: 'luis.intentLabelingSidePane.noMatchText',
        defaultMessage: "We didn't find any matches."
    },
    createEntity: {
        id: 'luis.intentLabelingSidePane.createEntity',
        defaultMessage: 'Create an entity'
    }
});

const { styleComponent } = getStylistV2('IntentLabelingSidePane');
const scopedTheme = getScopedTheme('IntentLabelingSidePane');

const EntityTreeRoot = styleComponent<IStackProps>(Stack)('EntityTreeRoot', {
    position: 'relative'
});

const EmptyView = styleComponent<IStackProps>(Stack)('EmptyView', {
    fontSize: 14,
    padding: 12
});

const linkStyles = classNamesFunction<ILinkProps, ILinkStyles>()({
    root: {
        selectors: {
            '&:hover': { textDecoration: 'none' }
        }
    }
});

type EntityTreeContainerProps = {
    /**
     * List of entities to display in the tree.
     */
    entityStores: readonly EntityStore[];
    /**
     * Selected entity.
     */
    selectedEntityStore: EntityStore;
    /**
     * Indicates if the labeling is enabled.
     */
    isLabelingOn: boolean;
    /**
     * Callback for updating if labeling is enabled.
     */
    onToggleLabeling: (on: boolean) => void;
    /**
     * Callback for selecting an entity.
     */
    onSelectEntity: (selected: { entityId: LuisId; parentId: LuisId }) => void;
    /**
     * Callback for create new entity, when user has no entities.
     */
    onStartCreateEntity: () => void;
};

export const EntityTreeContainer = observer((props: EntityTreeContainerProps) => {
    const { entityStores, onStartCreateEntity, selectedEntityStore, onSelectEntity, isLabelingOn, onToggleLabeling } = props;

    const { createChildEntity, renameEntity, renameChildEntity, deleteEntity } = useEntityTreeActions();

    const { localize } = useLocalization();

    const [query, setQuery] = React.useState('');

    const [childEntityToDelete, setChildEntityToDelete] = React.useState<{ entity: SimpleEntityStore; rootId: LuisId }>({
        entity: null,
        rootId: null
    });

    const deleteEntityTracker = useProgressTracker({
        onComplete: () => {
            setChildEntityToDelete(null);
            setChildEntityToDelete({ rootId: null, entity: null });
        }
    });

    // [sorgh]: This is a hack to trick mobx into observing a recursive observable.
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    (entityStores.find(e => e.typeId === ModelType.Simple) as SimpleEntityStore)?.updateCount;

    const filteredEntities = React.useMemo(
        () =>
            query
                ? orderBy(
                      [
                          ...substringSearchStrategy(
                              entityStores.filter(e => e.typeId !== ModelType.Simple),
                              query,
                              'name'
                          ),
                          ...entityStores.filter(
                              e =>
                                  e.typeId === ModelType.Simple &&
                                  !!findFirstNodes(
                                      e as SimpleEntityStore,
                                      c => c.name.toLocaleLowerCase().indexOf(query.toLocaleLowerCase()) !== -1
                                  ).length
                          )
                      ],
                      'name',
                      'asc'
                  )
                : entityStores,
        [entityStores, query]
    );

    const handleDeleteEntity = (rootId: LuisId, entityToDelete: SimpleEntityStore) => {
        setChildEntityToDelete({ rootId, entity: entityToDelete });
    };

    /**
     * Custom rendering for entity tree items based on the search query.
     * @param item Entity tree item to render content for.
     */
    const renderItemContent = (item: EntityStore) => {
        const spans = getMatchedSpans(item.name, query);

        return spans.length
            ? spans.map((span, idx) =>
                  span.matched ? (
                      <LuisText fontWeight={700} key={`${idx}`}>
                          {span.text}
                      </LuisText>
                  ) : (
                      span.text
                  )
              )
            : item.name;
    };

    const selectEntity = ({ entityId, parentId }: { entityId: LuisId; parentId: LuisId }) => {
        const selectedStore = entityStores.find(e => e.id === entityId);
        if (parentId === entityId && selectedStore.typeId === ModelType.Hierarchical) {
            entityId = (selectedStore as HierarchicalEntityStore).children[0].id;
        }
        onSelectEntity({ entityId, parentId });
    };

    return (
        <Stack verticalFill>
            <EntityTreeTopBar
                query={query}
                onChangeQuery={setQuery}
                isLabelingOn={isLabelingOn}
                disabled={!entityStores.length}
                onToggleLabelingButton={onToggleLabeling}
                onStartCreateEntity={onStartCreateEntity}
                toggleLabelingButtonDisabled={!selectedEntityStore}
            />

            <EntityTreeRoot verticalFill grow>
                {entityStores.length ? (
                    filteredEntities.length ? (
                        <ScrollablePane>
                            <EntityTree
                                allowRootEntityDeletion
                                onSelectEntity={selectEntity}
                                onRenameEntity={renameEntity}
                                onDeleteEntity={handleDeleteEntity}
                                entityStores={filteredEntities.slice()}
                                onCreateChildEntity={createChildEntity}
                                onRenameChildEntity={renameChildEntity}
                                onRenderEntityContent={renderItemContent}
                                selectedEntityId={selectedEntityStore?.id}
                            />
                        </ScrollablePane>
                    ) : (
                        <EmptyView>
                            <LuisText color={scopedTheme.emptyTextColor}>{localize(messages.noMatchText)}</LuisText>
                        </EmptyView>
                    )
                ) : (
                    <EmptyView tokens={{ childrenGap: 8 }}>
                        <LuisText color={scopedTheme.emptyTextColor}>{localize(messages.emptyText)}</LuisText>
                        <Link styles={linkStyles} onClick={onStartCreateEntity}>
                            {localize(messages.createEntity)}
                        </Link>
                    </EmptyView>
                )}
            </EntityTreeRoot>

            {childEntityToDelete.entity && (
                <DeleteEntityModal
                    hidden={false}
                    disabled={deleteEntityTracker.isRunning}
                    entityNames={[childEntityToDelete.entity.name]}
                    onDismiss={() => setChildEntityToDelete({ rootId: null, entity: null })}
                    onDelete={() => deleteEntity(childEntityToDelete.rootId, childEntityToDelete.entity.id, deleteEntityTracker)}
                />
            )}
        </Stack>
    );
});
